import { useState, useEffect } from "react";
import { message } from "antd";
import {
  fetchQuizByCourse,
  createQuiz,
  updateQuiz,
  fetchQuestionsByQuiz,
  createQuestion,
  updateQuestion,
  deleteQuizQuestion,
  fetchQuizByCourseAdmin,
} from "../../service/AdminService/Quizservice";

export const createBlankQuestion = (displayId) => ({
  id: Date.now(),
  displayId,
  questionText: "",
  questionType: "MCQ_SINGLE",
  options: [
    { id: Date.now(), text: "", isCorrect: false },
    { id: Date.now() + 1, text: "", isCorrect: false },
  ],
  correctAnswer: [],
  points: 10,
  explanation: "",
  isSaved: false,
  required: false,
});

const transformApiQuestion = (q, index) => {
  let optionsArray = [];
  let correctAnswersArray = [];
  try { optionsArray = q.options ? JSON.parse(q.options) : []; } catch (e) { console.error(e); }
  try { correctAnswersArray = q.correctAnswer ? JSON.parse(q.correctAnswer) : []; } catch (e) { console.error(e); }
  return {
    id: q.questionId,
    displayId: index + 1,
    questionText: q.questionText || "",
    questionType: q.questionType || "MCQ_SINGLE",
    options: optionsArray.map((text) => ({ id: Date.now() + Math.random(), text, isCorrect: correctAnswersArray.includes(text) })),
    correctAnswer: correctAnswersArray,
    points: q.points || 10,
    explanation: q.explanation || "",
    isSaved: true,
    required: q.required ?? false,
  };
};

export const useQuiz = (courseId) => {
  const [quiz, setQuiz] = useState(null);
  const [quizMeta, setQuizMeta] = useState({ title: "", description: "", timeLimit: 0, passingScore: 0, attemptsAllowed: 0, totalQuestions: 0, showResults: true, randomizeQuestions: true });
  const [questions, setQuestions] = useState([createBlankQuestion(1)]);
  const [loading, setLoading] = useState(false);

  const loadQuizData = async () => {
    try {
      setLoading(true);
      const quizData = await fetchQuizByCourseAdmin(courseId)
      setQuiz(quizData);
      if (quizData) {
        setQuizMeta({ title: quizData.title || "", description: quizData.description || "", timeLimit: quizData.timeLimit || 0, passingScore: quizData.passingScore || 0, attemptsAllowed: quizData.attemptsAllowed || 0, totalQuestions: quizData.questionsToShow || 0, showResults: quizData.showResults || false, randomizeQuestions: quizData.randomizeQuestions || false });
      }
    } catch (err) { setQuiz(null); } finally { setLoading(false); }
  };

  const loadQuestions = async (quizId) => {
  try {
    const apiQuestions = await fetchQuestionsByQuiz(quizId);
    if (apiQuestions.length === 0) {
      setQuestions([createBlankQuestion(1)]);
      return;
    }
    const transformed = apiQuestions.map(transformApiQuestion);
    setQuestions(transformed); 
  } catch (err) { 
    message.error(err.message); 
  }
};

  const saveQuiz = async () => {
    const payload = { title: quizMeta.title, description: quizMeta.description, timeLimit: quizMeta.timeLimit, attemptsAllowed: quizMeta.attemptsAllowed, questionsToShow: quizMeta.totalQuestions, passingScore: quizMeta.passingScore, showResults: quizMeta.showResults, randomizeQuestions: quizMeta.randomizeQuestions, ...(!quiz && { parentId: courseId, parentType: "course" }) };
    try {
      if (quiz) { await updateQuiz(quiz.quizId, payload); message.success("Quiz updated successfully"); }
      else { const newQuiz = await createQuiz(payload); setQuiz(newQuiz); await loadQuizData(); message.success("Quiz created successfully"); }
    } catch (err) { message.error(err.message); }
  };

  const saveQuestion = async (currentQ, isUpdate, quizId, currentIndex) => {
    const payload = { ...(!isUpdate && { quizId }), ...(isUpdate && { position: currentIndex + 1 }), questionText: currentQ.questionText, questionType: currentQ.questionType, required: currentQ.required ?? false, options: currentQ.questionType === "SHORT_ANSWER" ? null : JSON.stringify(currentQ.options.map((opt) => opt.text)), correctAnswer: currentQ.questionType === "SHORT_ANSWER" ? JSON.stringify([currentQ.correctAnswer]) : JSON.stringify(currentQ.correctAnswer), points: currentQ.points, explanation: currentQ.explanation };
    try {
      if (isUpdate) { await updateQuestion(currentQ.id, payload); message.success("Question updated successfully"); }
      else { await createQuestion(payload); message.success("Question added successfully"); }
      await loadQuestions(quizId);
    } catch (err) { message.error(err.message); throw err; }
  };

  const removeQuestion = async (questionId, quizId) => {
    try { await deleteQuizQuestion(questionId); message.success("Question deleted successfully"); await loadQuestions(quizId); }
    catch (err) { message.error(err.message); throw err; }
  };

  useEffect(() => { loadQuizData(); }, [courseId]);
  useEffect(() => { if (quiz?.quizId) loadQuestions(quiz.quizId); }, [quiz]);

  return { quiz, quizMeta, setQuizMeta, questions, setQuestions, loading, loadQuestions, saveQuiz, saveQuestion, removeQuestion };
};